import { Blob, Country } from '@consigli/types';

import { getStructure } from '@/pages/insight/folders/util/folders-structure';

import { mappingDict, FolderStructureStandards } from './foldername-mappings';

/**
 * Tree type representing an NS3451 or other standard tree directory. This is a special tree type
 * that has some specific traits:
 *
 * 1. All nodes are either a File or a Folder, but all true leaf nodes are files.
 * 2. A leaf folder is any parent of a File, as all files are at the bottom.
 */
export type Tree = {
  children: FileCollection;
};

export type TreeEntry = TreeFile | TreeFolder;
type NodeProps = {
  id: string;
  level?: number;
  toDisplayString: () => string;
};

export type FileCollection = Map<string, TreeEntry>;
export type TreeFolder = NodeProps & { children: FileCollection };
export type TreeFile = NodeProps & { blob: Blob };

export function isTreeFolder(node: TreeEntry): node is TreeFolder {
  return 'children' in node;
}

export function isTreeFile(node: TreeEntry): node is TreeFile {
  return 'blob' in node;
}

export type TreeNode = {
  id: string;
  level?: number;
  children: Map<string, TreeNode>;
  toDisplayString?: () => string;
};

/**
 * Perform a top-down traversal of the entire tree. Child ordering is determined by the
 * insertion order of the child map.
 */
export function visit(tree: Tree, onEntry: (entry: TreeEntry) => void) {
  function visitChild(child: TreeEntry) {
    onEntry(child);
    if (isTreeFolder(child)) {
      child.children.forEach(visitChild);
    }
  }
  tree?.children?.forEach(visitChild);
}

/**
 * Get all non-leaf nodes in the tree. For our specialized tree format this means
 * getting all the folders.
 */
export function getNonLeafNodes(tree: Tree): TreeFolder[] {
  const entries: TreeFolder[] = [];
  visit(tree, (entry) => {
    if (isTreeFolder(entry)) {
      entries.push(entry);
    }
  });
  return entries;
}

export function createTreeFile(blob: Blob): TreeFile {
  return {
    toDisplayString: () => blob.name,
    id: blob.id,
    blob,
  };
}

export function isLeafNode(node: TreeFolder): boolean {
  for (const child of node.children.values()) {
    if (isTreeFolder(child)) {
      return false;
    }
  }
  return true;
}

// Helper function to determine if a node is a child of a parent
const isChildOf = (childId: string, parentId: string): boolean => {
  // For NS3451: check if child follows the hierarchical pattern
  if (childId.includes('NS3451')) {
    // NS3451_1 -> NS3451_11 (parent: NS3451_1, child: NS3451_11)
    // NS3451_11 -> NS3451_111 (parent: NS3451_11, child: NS3451_111)

    // Extract the numeric parts after NS3451_
    const parentNumeric = parentId.replace('NS3451_', '');
    const childNumeric = childId.replace('NS3451_', '');

    // Child should start with parent's numeric part and be longer
    // This creates the hierarchical relationship: 1 -> 11 -> 111
    return childNumeric.startsWith(parentNumeric) && childNumeric.length > parentNumeric.length;
  }

  // For OMUK: check if child starts with parent + underscore
  if (childId.includes('OMUK')) {
    return childId.startsWith(parentId + '_');
  }

  // Fallback: assume underscore-based hierarchy
  return childId.startsWith(parentId + '_');
};

const updateTreeStructure = (files: Record<string, TreeNode>) => {
  const treeStructure = new Map();

  // Get level 1 (root) folders
  const levelOneFolders = Object.values(files).filter((file) => file.level === 1);

  levelOneFolders.forEach((rootNode) => {
    treeStructure.set(rootNode.id, rootNode);

    // Get level 2 folders that belong to this root
    const levelTwoFolders = Object.values(files).filter((file) => {
      if (file.level !== 2) return false;

      // For NS3451: NS3451_11 should be child of NS3451_1
      // For OMUK: OMUK_1_1 should be child of OMUK_1
      return isChildOf(file.id, rootNode.id);
    });

    levelTwoFolders.forEach((levelTwoFolder) => {
      rootNode.children.set(levelTwoFolder.id, levelTwoFolder);

      // Get level 3 folders that belong to this level 2 folder
      const levelThreeFolders = Object.values(files).filter((file) => {
        if (file.level !== 3) return false;

        return isChildOf(file.id, levelTwoFolder.id);
      });

      levelThreeFolders.forEach((levelThreeFolder) => {
        levelTwoFolder.children.set(levelThreeFolder.id, levelThreeFolder);
      });
    });
  });

  return { children: treeStructure };
};

/**
 * Function for mapping category codes to their corresponding folder names
 * This avoids using the translation system, as we always want norwegian titles for norwegian standards and so on.
 */
export const getFolderNameMappings = (structure: Record<string, TreeNode>, country: Country) => {
  let chosenStandard;

  if (country === Country.NORWAY) {
    chosenStandard = FolderStructureStandards.NS3451;
  } else {
    // Use OMUK as the default standard for UK and all other countries
    chosenStandard = FolderStructureStandards.OMUK;
  }

  const standard = mappingDict[chosenStandard as keyof typeof mappingDict];
  //use the mappingDict to translate the folder names
  Object.values(structure).filter(
    (file) =>
      (file.toDisplayString = () => {
        return standard[file.id as keyof typeof standard] || file.id;
      }),
  );
  return structure;
};

/**
 * Creates a tree structure from the structure object
 * @returns The tree structure
 */
export const createTree = (country: Country): { children: Map<string, TreeNode> } => {
  const structure = getFolderNameMappings(getStructure(country), country);
  return updateTreeStructure(structure);
};
